import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  PaginationState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ReactNode, useEffect, useRef, useState } from "react";

type AdvancedOperator = "contains" | "equals" | "gt" | "lt";

const advancedFilterFn: FilterFn<unknown> = (row, columnId, filterValue) => {
  if (!filterValue) return true;
  const value = filterValue as { op?: AdvancedOperator; value?: string };
  if (!value.value) return true;
  const cellValue = row.getValue(columnId);
  const cellString =
    cellValue === null || cellValue === undefined ? "" : String(cellValue);
  const filterString = String(value.value);
  const cellNumber = Number(cellValue);
  const filterNumber = Number(value.value);
  const bothNumbers = !Number.isNaN(cellNumber) && !Number.isNaN(filterNumber);
  const op = value.op || "contains";

  if (op === "equals") {
    if (bothNumbers) {
      return cellNumber === filterNumber;
    }
    return cellString.toLowerCase() === filterString.toLowerCase();
  }

  if (op === "gt") {
    if (!bothNumbers) return false;
    return cellNumber > filterNumber;
  }

  if (op === "lt") {
    if (!bothNumbers) return false;
    return cellNumber < filterNumber;
  }

  return cellString.toLowerCase().includes(filterString.toLowerCase());
};

interface DataTableProps<TData extends object> {
  columns: ColumnDef<TData, any>[];
  data: TData[];
  pageSize?: number;
  loading?: boolean;
  dense?: boolean;
  footer?: ReactNode;
}

export function DataTable<TData extends object>({
  columns,
  data,
  pageSize = 50,
  loading,
  dense,
  footer,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [activeFilterColumnId, setActiveFilterColumnId] = useState<
    string | null
  >(null);
  const [filterOperator, setFilterOperator] =
    useState<AdvancedOperator>("contains");
  const [filterValue, setFilterValue] = useState("");
  const scrollTrackRef = useRef<HTMLDivElement | null>(null);
  const headerScrollRef = useRef<HTMLDivElement | null>(null);
  const bodyScrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollWidth, setScrollWidth] = useState(0);

  const table = useReactTable<TData>({
    data,
    columns,
    defaultColumn: {
      filterFn: advancedFilterFn,
    },
    getRowId: (row: any, index) =>
      row?.id !== undefined && row?.id !== null ? String(row.id) : String(index),
    autoResetPageIndex: false,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      columnFilters,
      pagination,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    initialState: {
      columnPinning: {
        left: ["brand_name"],
        right: [],
      },
    },
  });

  const rows = table.getPaginationRowModel().rows;
  const filteredRowCount = table.getFilteredRowModel().rows.length;
  const openFilter = Boolean(filterAnchorEl);
  const activeColumn = activeFilterColumnId
    ? table.getColumn(activeFilterColumnId)
    : null;

  useEffect(() => {
    if (bodyScrollRef.current) {
      setScrollWidth(bodyScrollRef.current.scrollWidth);
    }
  }, [data, columns]);

  useEffect(() => {
    if (headerScrollRef.current) {
      headerScrollRef.current.scrollTop = 0;
    }
    if (scrollTrackRef.current) {
      scrollTrackRef.current.scrollTop = 0;
    }
    if (bodyScrollRef.current) {
      bodyScrollRef.current.scrollTop = 0;
    }
  }, [pagination.pageIndex]);

  const handleOpenFilter = (
    event: React.MouseEvent<HTMLElement>,
    columnId: string,
  ) => {
    setFilterAnchorEl(event.currentTarget);
    setActiveFilterColumnId(columnId);
    const currentValue = table.getColumn(columnId)?.getFilterValue() as {
      op?: AdvancedOperator;
      value?: string;
    } | null;
    setFilterOperator(currentValue?.op || "contains");
    setFilterValue(currentValue?.value || "");
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
    setActiveFilterColumnId(null);
  };

  const handleApplyFilter = () => {
    if (activeColumn) {
      if (!filterValue) {
        activeColumn.setFilterValue(undefined);
      } else {
        activeColumn.setFilterValue({
          op: filterOperator,
          value: filterValue,
        });
      }
    }
    handleCloseFilter();
  };

  const handleClearFilter = () => {
    if (activeColumn) {
      activeColumn.setFilterValue(undefined);
    }
    setFilterValue("");
  };

  return (
    <>
      <Box>
        <TableContainer
          ref={headerScrollRef}
          sx={{
            overflowX: "auto",
          }}
          onScroll={(event) => {
            const left = (event.currentTarget as HTMLDivElement).scrollLeft;
            if (scrollTrackRef.current) {
              scrollTrackRef.current.scrollLeft = left;
            }
            if (bodyScrollRef.current) {
              bodyScrollRef.current.scrollLeft = left;
            }
          }}
        >
          <Table size={dense ? "small" : "medium"}>
            <TableHead
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 2,
                backgroundColor: "#f7fafc",
                "& .MuiTableCell-root": {
                  color: "#24324D",
                  fontWeight: 600,
                },
              }}
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const isPinnedLeft = header.column.getIsPinned() === "left";
                    return (
                      <TableCell
                        key={header.id}
                        sx={
                          isPinnedLeft
                            ? {
                                position: "sticky",
                                left: 0,
                                zIndex: 1,
                                backgroundColor: "#f7fafc",
                              }
                            : undefined
                        }
                      >
                        {header.isPlaceholder ? null : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            {header.column.getCanSort() ? (
                              <TableSortLabel
                                active={!!header.column.getIsSorted()}
                                direction={
                                  (header.column.getIsSorted() as
                                    | "asc"
                                    | "desc"
                                    | false) === "desc"
                                    ? "desc"
                                    : "asc"
                                }
                                onClick={header.column.getToggleSortingHandler()}
                              >
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                              </TableSortLabel>
                            ) : (
                              flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )
                            )}
                            {header.column.getCanFilter() && (
                              <IconButton
                                size="small"
                                onClick={(event) =>
                                  handleOpenFilter(event, header.column.id)
                                }
                                sx={{ ml: 0.5 }}
                              >
                                <FilterListIcon
                                  fontSize="small"
                                  color={
                                    header.column.getIsFiltered()
                                      ? "primary"
                                      : "inherit"
                                  }
                                />
                              </IconButton>
                            )}
                          </Box>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableHead>
          </Table>
        </TableContainer>
        <TableContainer
          ref={bodyScrollRef}
          sx={{
            overflowX: "hidden",
            maxHeight: "calc(100vh - 400px)",
            overflowY: "auto",
          }}
        >
          <Table size={dense ? "small" : "medium"}>
            <TableBody>
              {loading && rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={table.getAllLeafColumns().length || 1}
                    sx={{
                      textAlign: "center",
                      py: 4,
                    }}
                  >
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : !loading && rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={table.getAllLeafColumns().length || 1}
                    sx={{
                      textAlign: "center",
                      py: 4,
                      color: "#6b7280",
                    }}
                  >
                    <Typography variant="body2">
                      Nenhum dado encontrado.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      height: dense ? 32 : 40,
                      "&:hover": {
                        backgroundColor: "#f1f5f9",
                      },
                    }}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const isPinnedLeft = cell.column.getIsPinned() === "left";
                      return (
                        <TableCell
                          key={cell.id}
                          size="small"
                          sx={{
                            whiteSpace: "normal",
                            lineHeight: 1.1,
                            fontSize: dense ? "0.8rem" : "0.85rem",
                            color: "#1e293b",
                            paddingTop: dense ? "4px" : "6px",
                            paddingBottom: dense ? "4px" : "6px",
                            ...(isPinnedLeft
                              ? {
                                  position: "sticky",
                                  left: 0,
                                  zIndex: 1,
                                  backgroundColor: "#ffffff",
                                }
                              : {}),
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <TablePagination
        component="div"
        count={filteredRowCount}
        page={table.getState().pagination.pageIndex}
        onPageChange={(_, newPage) => table.setPageIndex(newPage)}
        rowsPerPage={table.getState().pagination.pageSize}
        onRowsPerPageChange={(event) => {
          const newSize = parseInt(event.target.value, 10);
          table.setPageSize(newSize);
          table.setPageIndex(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />
      {footer}
      <Popover
        open={openFilter}
        anchorEl={filterAnchorEl}
        onClose={handleCloseFilter}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box sx={{ p: 2, minWidth: 240 }}>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="filter-operator-label">Operador</InputLabel>
            <Select
              labelId="filter-operator-label"
              value={filterOperator}
              label="Operador"
              onChange={(event) =>
                setFilterOperator(event.target.value as AdvancedOperator)
              }
            >
              <MenuItem value="contains">Contém</MenuItem>
              <MenuItem value="equals">Igual a</MenuItem>
              <MenuItem value="gt">Maior que</MenuItem>
              <MenuItem value="lt">Menor que</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel shrink>Valor</InputLabel>
            <Box sx={{ mt: 3 }}>
              <input
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  borderRadius: 4,
                  border: "1px solid #cbd5e1",
                }}
                value={filterValue}
                onChange={(event) => setFilterValue(event.target.value)}
              />
            </Box>
          </FormControl>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <Button onClick={handleClearFilter} size="small">
              Limpar
            </Button>
            <Button
              variant="contained"
              onClick={handleApplyFilter}
              size="small"
            >
              Aplicar
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
